/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import { useCallback, useRef } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { useScreenRefresh } from './useScreenRefresh';
import {
  useRegisterTabScreenRefresh,
  useShellScreenHeaderRefresh,
} from '../../navigation/TabScreenRefreshContext';
import type { ScreenRefreshResult } from '../screenRefreshFeedback';

type LoadFn = (opts?: { force?: boolean }) => Promise<ScreenRefreshResult | void>;

type UseScreenLoadOptions = {
  /** Recarga al enfocar la pantalla (default: true). */
  refreshOnFocus?: boolean;
  /** Toast de feedback tras refresh (default: true). */
  feedback?: boolean;
  /**
   * Si el último load OK fue hace menos de N ms, el focus no recarga.
   * El refresh manual (header/pull) siempre recarga con force.
   * Pensado para pantallas no-operativas (historiales, reportes, settings);
   * las colas vivas (Pedidos, Cocina, Mesas) dependen del focus-refetch.
   */
  minFocusIntervalMs?: number;
};

/**
 * Hook unificado que estandariza load-on-focus + refresh-con-force en todas las pantallas.
 *
 * - **Focus** → `loadFn()` (sin force, cache ok)
 * - **Refresh** (botón header / pull-to-refresh) → `loadFn({ force: true })` (bypass cache)
 * - Guard de reentrada, estado `refreshing`, toast — delegado a `useScreenRefresh`.
 *
 * Usar `useScreenLoad` para tabs principales (POS, Catalog, etc.) que registran via `useRegisterTabScreenRefresh`.
 * Usar `useScreenLoadShell` para pantallas de settings/shell que registran via `useShellScreenHeaderRefresh`.
 */
function useScreenLoadInner(
  loadFn: LoadFn,
  options?: UseScreenLoadOptions
) {
  const refreshOnFocus = options?.refreshOnFocus ?? true;
  const feedback = options?.feedback ?? true;
  const minFocusIntervalMs = options?.minFocusIntervalMs ?? 0;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const lastLoadOkAtRef = useRef(0);

  const runLoad = useCallback(async (opts?: { force?: boolean }) => {
    const result = await loadFnRef.current(opts);
    lastLoadOkAtRef.current = Date.now();
    return result;
  }, []);

  const { refreshing, onRefresh } = useScreenRefresh(
    async () => runLoad({ force: true }),
    { feedback }
  );

  useFocusEffect(
    useCallback(() => {
      if (!refreshOnFocus) return;
      if (
        minFocusIntervalMs > 0 &&
        Date.now() - lastLoadOkAtRef.current < minFocusIntervalMs
      ) {
        return;
      }
      void runLoad();
    }, [minFocusIntervalMs, refreshOnFocus, runLoad])
  );

  const reload = useCallback(
    (opts?: { force?: boolean }) => runLoad(opts),
    [runLoad]
  );

  return {
    refreshing,
    onRefresh,
    /** Load sin force, para uso programático manual. */
    reload,
  };
}

/** Para tabs principales (POS, Catalog, Inventory, Reports, Customer, MoreHub). */
export function useScreenLoad(
  routeName: string,
  loadFn: LoadFn,
  options?: UseScreenLoadOptions
) {
  const result = useScreenLoadInner(loadFn, options);
  useRegisterTabScreenRefresh(routeName, result.onRefresh);
  return result;
}

/** Para pantallas de settings/shell (Caja, BranchSettings, Profile, etc.). */
export function useScreenLoadShell(
  routeName: string,
  loadFn: LoadFn,
  options?: UseScreenLoadOptions
) {
  const result = useScreenLoadInner(loadFn, options);
  useShellScreenHeaderRefresh(routeName, result.onRefresh);
  return result;
}
